import axios from "axios";
import { parseNewNewYorkApiResponse } from "../../utils";
import { PromiseHandler } from "../../handlers";
import { FilterTypes, parseIdFilter } from "../../handlers/filter";
import { NotImplementedError } from "../../errors";
import { AudienceLinkCodeGenerationError, AudienceLinksFetchError, AudienceLinkToggleError, UpdateAudienceLinkDeliveryPathError, BulkAudienceLinkToggleError } from "./errors";
import { serviceAudienceLinkInfoToAudienceLinkInfo, serviceGenerateLinkCodeResponseToGenerateLinkCodeResponse, deliveryPathRequestToServiceDeliveryPathRequest, LinkCodeAction, LinkStatusAction } from "../../models/audienceLinks";
/**
 * Handler for audience links functionality
 */
export default class AudienceLinksHandler extends PromiseHandler {
    /**
     * Creates a new instance of AudienceLinksHandler
     * @param sdk The MadSDK instance
     */
    constructor(sdk) {
        super(sdk, "audienceLinks");
    }
    /**
     * Find audience links based on provided filters
     * @param filters Required filters including organization ID and optional DMP ID
     * @returns Promise resolving to an array of audience links
     */
    async findItems(filters) {
        const idFilter = parseIdFilter(filters);
        if (idFilter.size === 0) {
            throw new Error("Organization ID is required for audience links find operation");
        }
        if (idFilter.size > 1) {
            throw new Error("Only one organization ID can be provided for audience links find operation");
        }
        const orgId = idFilter.values().next().value;
        // Check if DMP ID is specified in the filter
        const dmpIdFilter = filters?.where?.find((f) => f.field === "dmpId" && f.type === FilterTypes.EQ);
        const dmpId = dmpIdFilter?.value;
        // If DMP ID is specified, get a specific audience link
        if (dmpId) {
            return this.getAudienceLinkByDmpId(orgId, dmpId).then((link) => [link]);
        }
        // Otherwise, get all audience links for the organization
        return this.getAudienceLinks(orgId);
    }
    /**
     * Save audience link data
     * @param data The audience link data to save
     * @returns Promise resolving to the updated audience link
     */
    async saveItem(data) {
        if (!data.orgId || !data.dmpId) {
            throw new Error("Organization ID and DMP ID are required for audience link save operation");
        }
        const orgId = data.orgId;
        const dmpId = data.dmpId;
        const result = {
            ...data,
            orgId,
            dmpId
        };
        let hasUpdates = false;
        // Handle link code generation if the indicator is provided
        if (data.linkCode === LinkCodeAction.GENERATE) {
            const generateResult = await this.generateAudienceLinkCode(orgId, { dmpId });
            result.linkCode = generateResult.linkCode;
            hasUpdates = true;
        }
        else {
            // Handle delivery path update if properties are provided
            if (data.bucketName !== undefined &&
                data.objectPath !== undefined &&
                data.cloudProvider !== undefined) {
                await this.updateAudienceLinkDeliveryPath(orgId, dmpId, {
                    bucketName: data.bucketName,
                    objectPath: data.objectPath,
                    cloudProvider: data.cloudProvider
                });
                hasUpdates = true;
            }
            // Handle toggle if enabled flag is provided
            if (data.enabled !== undefined) {
                let enabledValue;
                // Check if it's a LinkStatusAction
                if (data.enabled === LinkStatusAction.ENABLE_LINK ||
                    data.enabled === LinkStatusAction.DISABLE_LINK) {
                    enabledValue = data.enabled === LinkStatusAction.ENABLE_LINK;
                    await this.toggleAudienceLink(orgId, dmpId, { enabled: enabledValue });
                    result.enabled = enabledValue; // Update the result with the actual boolean value
                    hasUpdates = true;
                }
            }
        }
        // If no updates were made, throw an error
        if (!hasUpdates) {
            throw new Error("Either delivery path properties, enabled flag, or link code generation request must be provided for audience link save operation");
        }
        return result;
    }
    /**
     * Delete is not implemented for audience links
     */
    async deleteItem() {
        throw new NotImplementedError("delete");
    }
    /**
     * Generate a new audience link code for a specific organization and DMP
     * @param orgId The organization ID
     * @param requestData The request data containing DMP ID
     * @returns A promise that resolves to the generated link code response
     */
    async generateAudienceLinkCode(orgId, requestData) {
        try {
            const url = `${this.sdk.urls.baseAPIUrl}/organization/${orgId}/audienceLink/generate`;
            const response = await axios.post(url, {
                dmp_id: requestData.dmpId
            }, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const { data: rawResponse } = parseNewNewYorkApiResponse(response);
            return serviceGenerateLinkCodeResponseToGenerateLinkCodeResponse(rawResponse);
        }
        catch (error) {
            throw new AudienceLinkCodeGenerationError(error);
        }
    }
    /**
     * Get all audience links for an organization
     * @param orgId The organization ID
     * @returns A promise that resolves to an array of audience link info objects
     */
    async getAudienceLinks(orgId) {
        try {
            const url = `${this.sdk.urls.baseAPIUrl}/organization/${orgId}/audienceLink`;
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const { data: rawAudienceLinks } = parseNewNewYorkApiResponse(response);
            // Convert the map to an array with dmpId and orgId included
            return Object.entries(rawAudienceLinks).map(([dmpId, linkInfo]) => serviceAudienceLinkInfoToAudienceLinkInfo(linkInfo, dmpId, orgId));
        }
        catch (error) {
            throw new AudienceLinksFetchError(error);
        }
    }
    /**
     * Get a specific audience link for an organization and DMP
     * @param orgId The organization ID
     * @param dmpId The DMP ID
     * @returns A promise that resolves to the audience link info
     */
    async getAudienceLinkByDmpId(orgId, dmpId) {
        try {
            const url = `${this.sdk.urls.baseAPIUrl}/organization/${orgId}/audienceLink/${dmpId}`;
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const { data: rawAudienceLink } = parseNewNewYorkApiResponse(response);
            return serviceAudienceLinkInfoToAudienceLinkInfo(rawAudienceLink, dmpId, orgId);
        }
        catch (error) {
            throw new AudienceLinksFetchError(error);
        }
    }
    /**
     * Toggle an audience link enabled/disabled
     * @param orgId The organization ID
     * @param dmpId The DMP ID
     * @param requestData The request data containing enabled flag
     * @returns A promise that resolves to an empty object on success
     * @throws AudienceLinkToggleError on failure
     */
    async toggleAudienceLink(orgId, dmpId, requestData) {
        try {
            const url = `${this.sdk.urls.baseAPIUrl}/organization/${orgId}/audienceLink/${dmpId}/toggle`;
            const response = await axios.post(url, {
                enabled: requestData.enabled
            }, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            // Parse response using parseNewNewYorkApiResponse
            parseNewNewYorkApiResponse(response);
            // If we get here without an error, the operation was successful
            return {};
        }
        catch (error) {
            // Throw error instead of returning failure response
            throw new AudienceLinkToggleError(error);
        }
    }
    /**
     * Update the delivery path for an audience link
     * @param orgId The organization ID
     * @param dmpId The DMP ID
     * @param requestData The request data containing delivery path details
     * @returns A promise that resolves to an empty object on success
     * @throws UpdateAudienceLinkDeliveryPathError on failure
     */
    async updateAudienceLinkDeliveryPath(orgId, dmpId, requestData) {
        try {
            const url = `${this.sdk.urls.baseAPIUrl}/organization/${orgId}/audienceLink/${dmpId}/deliveryPath`;
            const serviceRequest = deliveryPathRequestToServiceDeliveryPathRequest(requestData);
            const response = await axios.post(url, serviceRequest, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            // Parse response using parseNewNewYorkApiResponse
            parseNewNewYorkApiResponse(response);
            // If we get here without an error, the operation was successful
            return {};
        }
        catch (error) {
            // Throw error instead of returning failure response
            throw new UpdateAudienceLinkDeliveryPathError(error);
        }
    }
    /**
     * Bulk toggle multiple audience links for an organization
     * @param orgId The organization ID
     * @param requestData The request data containing array of links to toggle
     * @returns A promise that resolves to a map of DMP IDs to their enabled states
     */
    async bulkToggleAudienceLinks(orgId, requestData) {
        try {
            const url = `${this.sdk.urls.baseAPIUrl}/organization/${orgId}/audienceLink/bulkToggle`;
            // Transform the request to service format
            const serviceRequest = {
                links: requestData.links.map((link) => ({
                    dmp_id: link.dmpId,
                    enabled: link.enabled
                }))
            };
            const response = await axios.post(url, serviceRequest, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            // Parse response using parseNewNewYorkApiResponse
            const { data: rawResponse } = parseNewNewYorkApiResponse(response);
            // Return the response data directly as it's already a map of DMP IDs to enabled states
            return rawResponse;
        }
        catch (error) {
            throw new BulkAudienceLinkToggleError(error);
        }
    }
}
//# sourceMappingURL=index.js.map